import {
  Alert,
  Button,
  Divider,
  Group,
  Modal,
  NumberInput,
  Paper,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { rawApi } from '../../api/client';
import { type ApiError, fieldErrors } from '../../api/errors';
import { listKey } from '../../api/queryKeys';
import { EntitySelect } from '../../components/EntityField';
import { PaperPicker, type StoredPaper } from '../../components/ThePaperItself';
import {
  FILED_MONTH_REASON_FIELD,
  isFiledMonthWarning,
  WritingIntoAFiledMonth,
} from '../../components/WritingIntoAFiledMonth';
import { useEntityIndex } from '../../hooks/useEntityIndex';
import { useIslandToday } from '../../hooks/useSeedLocation';
import { enumOptions } from '../../lib/enums';
import {
  currencySymbol,
  formatDate,
  formatMoney,
  formatQty,
  type NumberInputValue,
  toIsoDate,
  toMinorUnits,
  typedNumber,
} from '../../lib/format';
import { notifyDone, notifyError } from '../../lib/notify';
import { ITEMS_PATH, LEDGER_PATH, STOCK_PATH, useStockItems } from '../inventory/stock';
import { ORDERS_PATH, orderQuery, outstandingQty } from './orders';
import { billsGoodsWereAlreadyBilledOn, shortfallOpenOn, useVendorBills } from './RecordCreditForm';

const RECEIPTS_PATH = '/admin/v1/suppliers/receipts';
const SUPPLIERS_PATH = '/admin/v1/suppliers/suppliers';
const PAYABLES_PATH = '/admin/v1/suppliers/payables';
/** The vendor page's own bill list, which shows settled ones too. */
const SUPPLIER_INVOICES_PATH = '/admin/v1/suppliers/supplier-invoices';
/** The owner's list of bills that do not agree with their delivery — a delivery can join or leave it. */
const DISCREPANCIES_PATH = '/admin/v1/suppliers/bill-discrepancies';
const FX_PATH = '/admin/v1/pricing/fx-rates';

/** FX is stored against a 1e6 scale so a rate like 26.712345 survives the round trip exactly. */
const FX_SCALE = 1_000_000;

interface Line {
  /** Lines are added and removed while the form is open, so each carries its own React key. */
  key: string;
  inventoryItemId: string;
  qty: NumberInputValue;
  /** What one unit cost on their invoice, in the money they billed in. */
  unitPrice: NumberInputValue;
  /**
   * The order line this is a delivery of, when it came off an order. Sent with the delivery so
   * "eight of the ten cases arrived" is a fact the order can state, rather than something a person
   * works out by comparing two screens — and so a short delivery leaves the order still on order.
   */
  purchaseOrderLineId?: string | null;
  /** What the order asked for in total, however many deliveries it has taken to bring. */
  orderedQty?: number;
  /**
   * What was STILL TO COME on that order line when this form opened. The box is filled with it, so
   * a short delivery is one number corrected rather than composed from nothing — and it is what a
   * short count on this delivery is short OF. On a first delivery the two are the same number; on
   * the second they are not, and confusing them is what `stillToCome` exists to stop.
   */
  stillDueQty?: number;
}

let keySeq = 0;
const blankLine = (): Line => ({ key: `line-${++keySeq}`, inventoryItemId: '', qty: '', unitPrice: '' });

interface SupplierRow {
  id: string;
  name: string;
  invoiceCurrency?: string | null;
  paymentTermsDays?: number | null;
}

interface OrderRow {
  id: string;
  supplierId: string;
  status: string;
  orderedAt?: string | null;
  expectedAt?: string | null;
  note?: string | null;
}

/** Today, as the plain calendar date the API stores. */

/**
 * Which explanations can be true of a difference with that sign.
 *
 * Held here as well as in `reasonFitsDifference` on the server, for the reason the payment
 * allowance is held in both places: the wrong ones are never offered, so the manager cannot pick
 * one and be told after the press. A charge only ever makes a bill BIGGER, and goods that never
 * turned up can only leave the paper bigger than the delivery — getting either backwards is the
 * difference between "they owe us three cases" and "we owe them for three cases".
 */
function reasonsFor(differenceMinor: number) {
  const impossible = differenceMinor > 0 ? ['overDelivery'] : ['shortDelivery', 'unexpectedCharge'];
  return enumOptions('billDiscrepancyReason').filter((o) => !impossible.includes(o.value));
}

/**
 * What THIS delivery was expected to bring on one line, and the two ways of saying it.
 *
 * A delivery is measured against what was still to come on the order, never against what the
 * order asked for in the first place. The second delivery against a part-received order is where
 * that matters and where it was wrong: 40 kg of the 46 arrived in May, 4 of the last 6 turn up
 * now, and measuring against 46 says "42 kg short" beside a figure of L 90.00 — which at L 45 a
 * kilo is two kilos. The money and the words then contradicted each other inside one box, and the
 * words are the half a manager can check against the pallet while the driver waits. The server has
 * always compared a delivery with what was outstanding (`flagAgainstOrder`); this says the same.
 *
 * Both phrasings are here rather than at the two call sites, because they have to agree: the line
 * that says "6 kg still to come" and the sentence that says what a short count is short of are the
 * same fact, a few inches apart.
 */
function stillToCome(line: Line, unit: string) {
  if (typeof line.stillDueQty !== 'number') return null;
  const ordered = typeof line.orderedQty === 'number' ? line.orderedQty : line.stillDueQty;
  // Thousandths, because these are decimal quantities held as floats: 3 × 0.1 kg is not 0.3.
  const alreadyArrived = Math.round((ordered - line.stillDueQty) * 1000) / 1000 > 0;
  return {
    qty: line.stillDueQty,
    /** Under the box holding what turned up. */
    expected: alreadyArrived
      ? `${formatQty(line.stillDueQty, unit)} still to come of the ${formatQty(ordered, unit)} ordered`
      : `${formatQty(ordered, unit)} ordered`,
    /** In a sentence: what a short count on this delivery is short OF. */
    against: alreadyArrived
      ? `the ${formatQty(line.stillDueQty, unit)} still to come`
      : `the ${formatQty(ordered, unit)} ordered`,
  };
}

/**
 * The difference said in goods as well as in money: "3 case of Salva Vida short of the 10 ordered".
 *
 * A figure on its own — L 1,350.00 — is a number the manager has to work backwards from while the
 * driver waits. What he can actually check against the pallet in front of him is the count, and
 * the count is already on the screen: the order filled these lines in and the box beside each one
 * says what was still to come. Only the lines that came off an order can say this; a delivery that
 * arrived unannounced has nothing to be short OF, and stays silent rather than guessing.
 */
function differenceInGoods(
  lines: readonly Line[],
  label: (id: string) => string,
  unitOf: (id: string) => string,
): { shortBy: number; said: string }[] {
  const said: { shortBy: number; said: string }[] = [];
  for (const line of lines) {
    if (!line.inventoryItemId) continue;
    const unit = unitOf(line.inventoryItemId);
    const due = stillToCome(line, unit);
    if (!due) continue;
    const arrived = typedNumber(line.qty) ?? 0;
    // Thousandths, for the same reason as above.
    const shortBy = Math.round((due.qty - arrived) * 1000) / 1000;
    if (shortBy === 0) continue;
    said.push({
      shortBy,
      said:
        shortBy > 0
          ? `${formatQty(shortBy, unit)} of ${label(line.inventoryItemId)} short of ${due.against}`
          : `${formatQty(-shortBy, unit)} of ${label(line.inventoryItemId)} more than ${due.against}`,
    });
  }
  return said;
}

/** Their terms turned into the day the bill falls due, so nobody counts thirty days on a calendar. */
function dueFromTerms(invoiceDate: string, termsDays: number | null | undefined): string {
  if (!invoiceDate || !termsDays) return '';
  const d = new Date(`${invoiceDate}T12:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  d.setDate(d.getDate() + termsDays);
  return toIsoDate(d) ?? '';
}

/**
 * Writing down a delivery as it comes off the truck.
 *
 * This is the only way stock ever ARRIVES. Without it every figure in the bar could go one way:
 * waste took things off the shelf, a move shifted them between shelves, and the sales at the register
 * drew them down — so the only way to put a case of rum on the books was to count the whole area
 * and let the correction do it, which throws away what it cost, who sold it and which bill it is
 * on. Everything downstream depended on this and had nowhere to start: the cost per unit on the
 * stock list never moved, no recipe could be priced from what is really being paid, no supplier
 * bill ever opened, and the shipping-over screen asked which deliveries a crossing covered when
 * there were no deliveries on file to point at.
 *
 * What is deliberately NOT asked for is freight. On this island the mainland supplier drops the
 * order at the ferry and it is the ferry that bills — a box here for "what they charged to ship
 * it" would be the wrong question for almost every delivery, and answering it would double-count
 * against the crossing recorded under Shipping over.
 */
export function ReceiveDeliveryForm({
  locationId,
  supplierId: fixedSupplierId,
  purchaseOrderId: fixedOrderId,
  onClose,
  onSaved,
}: {
  locationId?: string;
  /** Set when the delivery is being recorded from a vendor's page or from the order itself. */
  supplierId?: string;
  /** Set when it is being recorded from the order: then its lines arrive already filled in. */
  purchaseOrderId?: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const stock = useStockItems();
  const items = useEntityIndex('inventoryItem');

  const suppliersQuery = useQuery({
    queryKey: listKey(SUPPLIERS_PATH),
    queryFn: () => rawApi<SupplierRow[]>('GET', SUPPLIERS_PATH),
    staleTime: 60_000,
  });

  const ordersQuery = useQuery({
    queryKey: listKey(ORDERS_PATH),
    queryFn: () => rawApi<OrderRow[]>('GET', ORDERS_PATH),
    staleTime: 30_000,
  });

  // The rate somebody already entered on the exchange-rate screen, rather than a box the manager
  // has to fill from memory on every delivery. It is still editable: the rate that belongs on a
  // bill is the one that applied the day they billed you.
  const fxQuery = useQuery({
    queryKey: listKey(FX_PATH),
    queryFn: () => rawApi<{ rateMicro: number; effectiveAt: string }[]>('GET', FX_PATH),
    staleTime: 60_000,
  });
  const latestFx = fxQuery.data?.[0]?.rateMicro;

  const [supplierId, setSupplierId] = useState(fixedSupplierId ?? '');
  const [storageLocationId, setStorageLocationId] = useState('');
  const [purchaseOrderId, setPurchaseOrderId] = useState<string | null>(fixedOrderId ?? null);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  /**
   * The paper the driver hands over, photographed at the door.
   *
   * The delivery note is the only record of what was actually written down, and it is the piece of
   * paper most likely to be gone by the time the count and the invoice turn out to disagree.
   */
  const [paper, setPaper] = useState<StoredPaper | null>(null);
  /**
   * The date the paper is dated, defaulted from the BAR's calendar rather than the browser's.
   *
   * `toIsoDate(new Date())` is the reader's local day. Roatán is UTC−6 and the office is often not on
   * the island, so this is the sixth place the same six-hour defect has been found: a bill typed at
   * half past seven on the last night of a month was dated the first of the next one, and dropped out
   * of that month's ISV return. `useIslandToday` carries the reasoning.
   */
  const islandToday = useIslandToday();
  const [invoiceDate, setInvoiceDate] = useState(islandToday);
  const [dueDate, setDueDate] = useState('');
  const [currency, setCurrency] = useState('HNL');
  const [fxRate, setFxRate] = useState<NumberInputValue>('');
  const [lines, setLines] = useState<Line[]>([blankLine()]);
  /**
   * The total AS PRINTED on their invoice. Deliberately blank rather than pre-filled with what the
   * lines come to: a box already holding the right answer is a box nobody reads, and this one
   * exists precisely so that the day it disagrees is the day somebody notices. Blank means it
   * agrees, which is what the field being absent has always meant to the API.
   */
  const [invoiceTotal, setInvoiceTotal] = useState<NumberInputValue>('');
  /**
   * The bill these goods were ALREADY billed on, when this delivery is the missing cases turning up.
   *
   * Set, no bill is opened at all: the goods land on that bill's side of what arrived. It is the
   * other ending a shortfall has, and the only one that used to charge the bar twice for the same
   * beer — the second delivery opened a second payable, so L 4,500.00 of goods were owed at
   * L 5,850.00 with both bills matching their own paperwork.
   */
  const [alreadyBilledOn, setAlreadyBilledOn] = useState<string | null>(null);
  const [discrepancyReason, setDiscrepancyReason] = useState<string | null>(null);
  const [discrepancyNote, setDiscrepancyNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  /** True once the manager has touched the due date, so their answer is never overwritten. */
  const [dueTouched, setDueTouched] = useState(false);

  const supplier = (suppliersQuery.data ?? []).find((s) => s.id === supplierId);
  const rate = typedNumber(fxRate) ?? (latestFx ?? 0) / FX_SCALE;

  // Their own money and their own terms, from their record, because that is what will be on the
  // paper in the manager's hand. Both stay editable — a supplier who normally bills in lempiras
  // occasionally sends a dollar invoice, and that must not need a trip to the supplier screen.
  const pickSupplier = (id: string | null) => {
    setSupplierId(id ?? '');
    setPurchaseOrderId(null);
    // Another vendor's shortfall is not this delivery's to settle.
    setAlreadyBilledOn(null);
    const chosen = (suppliersQuery.data ?? []).find((s) => s.id === id);
    if (chosen?.invoiceCurrency) setCurrency(chosen.invoiceCurrency);
    if (!dueTouched) setDueDate(dueFromTerms(invoiceDate, chosen?.paymentTermsDays));
  };

  // Their money and their terms when the supplier was decided by the screen this opened from, and
  // so was never picked here. Only until the manager touches either.
  const [defaultsFrom, setDefaultsFrom] = useState<string | null>(null);
  useEffect(() => {
    if (!supplier || defaultsFrom === supplier.id) return;
    setDefaultsFrom(supplier.id);
    if (supplier.invoiceCurrency) setCurrency(supplier.invoiceCurrency);
    if (!dueTouched) setDueDate(dueFromTerms(invoiceDate, supplier.paymentTermsDays));
  }, [supplier, defaultsFrom, dueTouched, invoiceDate]);

  const openOrders = (ordersQuery.data ?? []).filter(
    (o) => o.supplierId === supplierId && (o.status === 'sent' || o.status === 'partiallyReceived'),
  );

  /**
   * The order, once one has been chosen — and the end of typing a delivery in a second time.
   *
   * Choosing an order used to set an identifier and nothing else, so every line of a twenty-line
   * restock was re-picked from a dropdown of every stock item the business buys and re-priced off
   * the paper, having been typed once already when the order went out. What is filled in is what
   * is still OUTSTANDING, so a second delivery against the same order offers the remainder rather
   * than the whole order again.
   */
  const orderDetail = useQuery({ ...orderQuery(purchaseOrderId ?? ''), staleTime: 0 });
  const [filledFrom, setFilledFrom] = useState<string | null>(null);
  useEffect(() => {
    const detail = orderDetail.data;
    if (!detail || detail.po.id !== purchaseOrderId || filledFrom === detail.po.id) return;
    setFilledFrom(detail.po.id);
    const fromOrder = detail.lines
      .filter((l) => outstandingQty(l) > 0)
      .map((l) => ({
        ...blankLine(),
        inventoryItemId: l.inventoryItemId,
        qty: outstandingQty(l),
        unitPrice: l.unitPriceMinor / 100,
        purchaseOrderLineId: l.id,
        orderedQty: l.qty,
        // What is still to come, kept beside what was ordered: the box is filled with this, and
        // this is what a short count on this delivery will be measured against.
        stillDueQty: outstandingQty(l),
      }));
    if (fromOrder.length === 0) return;
    // Anything already typed by hand stays: it is what turned up that was not on the order.
    setLines((current) => [
      ...fromOrder,
      ...current.filter((l) => l.inventoryItemId && !l.purchaseOrderLineId),
    ]);
    if (detail.lines[0]?.currency) setCurrency(detail.lines[0].currency);
  }, [orderDetail.data, purchaseOrderId, filledFrom]);

  /** Choosing a different order replaces what the last one filled in, rather than adding to it. */
  const pickOrder = (id: string | null) => {
    setPurchaseOrderId(id);
    setFilledFrom(null);
    setLines((current) => {
      const kept = current.filter((l) => l.inventoryItemId && !l.purchaseOrderLineId);
      return kept.length > 0 ? kept : [blankLine()];
    });
  };

  const filled = lines.filter((l) => l.inventoryItemId);
  const goodsTotalMinor = filled.reduce(
    (sum, l) => sum + Math.round((toMinorUnits(l.unitPrice) ?? 0) * (typedNumber(l.qty) ?? 0)),
    0,
  );

  /*
   * WHAT ARRIVED, AND WHAT WAS BILLED — two facts, both true at once.
   *
   * The lines above are the shelf: they set stock, landed cost and what a drink costs, and seven
   * cases is the only honest number there. `billedMinor` is the paper, and it is what the vendor
   * will chase and what their statement will say. Recording seven against an invoice for ten used
   * to be the only thing this form could do, and it left a payable that reconciled against
   * nothing and a credit next month with nothing to come off.
   */
  /*
   * …unless these goods were billed already, which is the third thing that can be true at the door
   * and the one that used to be unrecordable. The three cases that were missing in May arrive with
   * a delivery note and nothing to pay, so there is no paper to state and nothing to disagree with:
   * what changes is how much of THAT bill has now turned up.
   */
  const vendorBills = useVendorBills(supplierId, Boolean(supplierId));
  const shortfallChoices = billsGoodsWereAlreadyBilledOn(vendorBills.data ?? [], currency);
  // Held, not cleared by an effect: changing the currency can take the chosen bill out of the list,
  // and a bill in the other money must not travel with the delivery once it has stopped being one
  // the goods can come off.
  const settlesBillId = shortfallChoices.some((c) => c.value === alreadyBilledOn) ? alreadyBilledOn : null;
  const shortfallMinor = shortfallOpenOn(vendorBills.data ?? [], settlesBillId);

  const statedMinor = settlesBillId ? undefined : toMinorUnits(invoiceTotal);
  const billedMinor = statedMinor ?? goodsTotalMinor;
  const differenceMinor = billedMinor - goodsTotalMinor;
  const disagrees = statedMinor !== undefined && differenceMinor !== 0;
  const againstOrder = differenceInGoods(filled, items.label, stock.unitOf);
  const inGoods = disagrees ? againstOrder : [];
  /*
   * SHORT, AND NOBODY HAS SAID WHAT THE PAPER SAYS.
   *
   * The box above is deliberately blank, and blank has always meant "the invoice agrees with what
   * arrived". That is a safe default on a delivery nobody was expecting, and an unsafe one on the
   * exact morning this screen exists for: three cases are missing, the manager corrects the
   * quantity, presses the button, and a bill for seven opens against an invoice for ten with
   * nothing anywhere saying so. He is not being careless — the screen never asked.
   *
   * So where a delivery is short of what it was due to bring and the printed total has been left
   * alone, the screen says which of the two bills is about to open. It refuses nothing: a vendor
   * who invoices what he actually sent is ordinary here, and blank is the right answer then.
   */
  const shortAndUnstated =
    statedMinor === undefined && !settlesBillId ? againstOrder.filter((g) => g.shortBy > 0) : [];
  const reasonChoices = disagrees ? reasonsFor(differenceMinor) : [];
  // Held rather than cleared by an effect: correcting a quantity can flip the difference from
  // "they billed for more" to "more arrived", and the answer that was true a keystroke ago must
  // not travel with the delivery once it has stopped being possible.
  const reason = reasonChoices.some((o) => o.value === discrepancyReason) ? discrepancyReason : null;

  const setLine = (index: number, patch: Partial<Line>) =>
    setLines(lines.map((l, i) => (i === index ? { ...l, ...patch } : l)));

  const queryClient = useQueryClient();

  /**
   * The mainland grower's invoice follows the fruit by a week, so a delivery received today can be
   * dated into a month the accountant has already filed. The server refuses it once naming the
   * month, this takes the reason, and the same delivery goes again — see `WritingIntoAFiledMonth`.
   */
  const [filedMonthWarning, setFiledMonthWarning] = useState<ApiError | null>(null);

  const post = useMutation({
    mutationFn: (input: { filedMonthReason?: string } = {}) =>
      rawApi('POST', RECEIPTS_PATH, {
        locationId,
        supplierId,
        purchaseOrderId: purchaseOrderId || null,
        invoiceNumber: invoiceNumber.trim() || null,
        sourceDocumentId: paper?.id ?? null,
        invoiceCurrency: currency,
        fxRateMicro: Math.round(Number(rate) * FX_SCALE),
        // Nothing the goods supplier charged to ship: see the note on this component. A crossing
        // is its own bill from the carrier, recorded under Shipping over and spread from there.
        freightActualMinor: 0,
        allocationMethod: 'byValue',
        invoiceDate: invoiceDate || null,
        dueDate: dueDate || null,
        storageLocationId,
        // Only when somebody stated it. Left out, the bill is the lines — which is what this form
        // has always posted and what every other caller of this route still does.
        // Goods already billed open no bill, so they carry no printed total and nothing to
        // disagree with — the server refuses the two together for that reason.
        ...(settlesBillId ? { alreadyBilledOnInvoiceId: settlesBillId } : {}),
        ...(statedMinor === undefined
          ? {}
          : {
              invoiceTotalMinor: statedMinor,
              discrepancyReason: reason,
              discrepancyNote: discrepancyNote.trim() || null,
            }),
        lines: filled.map((l) => ({
          inventoryItemId: l.inventoryItemId,
          // What ties eight kilos received to the ten that were ordered. Without it a delivery
          // could only say the order was "received", whatever was actually on the truck.
          purchaseOrderLineId: l.purchaseOrderLineId ?? null,
          qty: Number(l.qty),
          unit: stock.unitOf(l.inventoryItemId),
          unitPriceMinor: toMinorUnits(l.unitPrice) ?? 0,
          currency,
        })),
        // Attached only once the server has asked for it — see the dialog for why never before.
        ...(input.filedMonthReason ? { [FILED_MONTH_REASON_FIELD]: input.filedMonthReason } : {}),
      }),
    onSuccess: () => {
      setFiledMonthWarning(null);
      // Receiving moves five things at once, and a screen still showing the old figure after it is
      // how a manager comes to believe the delivery did not save and enters it a second time.
      for (const key of [
        STOCK_PATH,
        LEDGER_PATH,
        ITEMS_PATH,
        RECEIPTS_PATH,
        PAYABLES_PATH,
        SUPPLIER_INVOICES_PATH,
        // A delivery that disagrees with its paper joins this list, and the missing cases turning
        // up take it off again — either way the screen behind must not still show the old answer.
        DISCREPANCIES_PATH,
        ORDERS_PATH,
      ]) {
        void queryClient.invalidateQueries({ queryKey: listKey(key) });
      }
      onSaved();
      onClose();
      notifyDone(
        'Delivery recorded',
        // The bill is what the PAPER said, which is not always what went on the shelf — so the
        // sentence says both, and says the difference is being kept rather than swallowed.
        settlesBillId
          ? // Nothing new is owed, and saying so is the point: the money was charged the first time.
            `${filled.length} ${filled.length === 1 ? 'item is' : 'items are'} on the shelf. Nothing new is owed — ${formatMoney(goodsTotalMinor, currency)} of what ${supplier?.name ?? 'they'} billed and had not delivered has now come.`
          : `${filled.length} ${filled.length === 1 ? 'item is' : 'items are'} on the shelf, and ${formatMoney(billedMinor, currency)} is now owed to ${supplier?.name ?? 'them'}.${
              disagrees
                ? ` That is ${formatMoney(Math.abs(differenceMinor), currency)} ${differenceMinor > 0 ? 'more than' : 'less than'} what arrived — it is on the list of bills that do not agree until it is dealt with.`
                : ''
            }`,
      );
    },
    onError: (err: Error) => {
      if (isFiledMonthWarning(err)) return setFiledMonthWarning(err);
      const byField = fieldErrors(err);
      const first = Object.values(byField)[0];
      if (first) setError(first);
      notifyError(err, 'record this delivery');
    },
  });

  const submit = () => {
    if (!supplierId) return setError('Say who delivered it');
    if (!storageLocationId) return setError('Say where it was put away');
    if (filled.length === 0) return setError('Add at least one thing that arrived');
    const noQty = filled.find((l) => (typedNumber(l.qty) ?? 0) <= 0);
    if (noQty) return setError(`Say how much ${items.label(noQty.inventoryItemId)} arrived`);
    const noPrice = filled.find((l) => typedNumber(l.unitPrice) === undefined);
    if (noPrice) {
      return setError(
        `Say what ${items.label(noPrice.inventoryItemId)} cost each — it is what sets the price of everything made with it`,
      );
    }
    // Sent on every line, and the only honest answer is the unit the item is already counted in.
    const noUnit = filled.find((l) => !stock.unitOf(l.inventoryItemId));
    if (noUnit) {
      return setError(
        `${items.label(noUnit.inventoryItemId)} has no unit set — fill in "Counted in" under Stock items first`,
      );
    }
    if (!rate || rate <= 0) return setError('An exchange rate is required');
    // The one question only the person holding the paper can answer, and the answer decides what
    // happens next: a credit to chase, a price to argue about, a charge to accept.
    if (disagrees && !reason) {
      return setError('Their invoice and what arrived do not agree — say what the difference is');
    }
    // The screen refuses the same set the server does, beside the box that caused it: only what
    // they billed for and did not send can come off that bill. Goods worth more than that are not
    // its goods — somebody is going to be invoiced for them, and they need a delivery of their own.
    if (settlesBillId && goodsTotalMinor > shortfallMinor) {
      return setError(
        `That bill was short ${formatMoney(shortfallMinor, currency)} and this comes to ${formatMoney(goodsTotalMinor, currency)}. Record the rest as a delivery of its own.`,
      );
    }
    const twice = filled.find(
      (l, i) => filled.findIndex((o) => o.inventoryItemId === l.inventoryItemId) !== i,
    );
    if (twice) {
      return setError(
        `${items.label(twice.inventoryItemId)} is on the delivery twice — put the whole amount on one line`,
      );
    }
    setError(null);
    post.mutate({});
  };

  return (
    <Modal
      opened
      onClose={onClose}
      title={fixedSupplierId && supplier ? `Delivery from ${supplier.name}` : 'Record a delivery'}
      size="xl"
      // Half-completed forms are lost to a stray click otherwise.
      closeOnClickOutside={false}
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Copy in what actually turned up and what their invoice says it cost. This puts the goods on the
          shelf, brings the cost per unit up to date everywhere it is used, and opens their bill under Bills
          to pay.
        </Text>

        <Group grow align="flex-start">
          {/* Not asked when the screen this opened from was already about one vendor. */}
          {!fixedSupplierId && (
            <EntitySelect
              // Who SOLD it, which is never the carrier: what the ferry charged to bring it over is
              // a shipping charge of its own. See `entities.ts`.
              entity="goodsSupplier"
              label="Who delivered it"
              required
              value={supplierId || null}
              onChange={pickSupplier}
              disabled={post.isPending}
            />
          )}
          <EntitySelect
            entity="storageLocation"
            label="Where it was put away"
            description="Stock is counted separately in each area, so this decides which shelf figure goes up"
            required
            value={storageLocationId || null}
            onChange={(v) => setStorageLocationId(v ?? '')}
            disabled={post.isPending}
          />
        </Group>

        {/* Only when there is something to attach it to. A picker reading "no open orders" on every
            delivery is a question about machinery the manager did not ask about. And never when the
            order is what this was opened from: it is the subject, not a field. */}
        {!fixedOrderId && openOrders.length > 0 && (
          <Select
            label="Against an order"
            description="Ties this to an order you placed and fills in what was ordered. Leave blank for anything that arrived without one."
            placeholder="Not against an order"
            // Named by when it went out and what it was for. It used to lead with an expected
            // date that nothing in this system ever sets, so every order in the list read
            // identically and picking the right one was guesswork.
            data={openOrders.map((o) => ({
              value: o.id,
              label:
                [o.note, o.orderedAt ? `sent ${formatDate(o.orderedAt)}` : null]
                  .filter(Boolean)
                  .join(' · ') || 'An order with nothing written against it',
            }))}
            value={purchaseOrderId}
            onChange={pickOrder}
            clearable
            comboboxProps={{ withinPortal: true }}
            disabled={post.isPending}
          />
        )}

        {fixedOrderId && (
          <Text size="sm" c="dimmed">
            {orderDetail.isLoading
              ? 'Fetching what was ordered…'
              : orderDetail.isError
                ? 'What was ordered could not be fetched, so nothing has been filled in. Add the lines by hand, or close this and try again.'
                : 'Filled in with what is still outstanding on this order. Correct any quantity they were short on, and add anything that turned up unordered.'}
          </Text>
        )}

        <Divider label="Their invoice" labelPosition="left" styles={{ label: { fontWeight: 600 } }} />

        <Group grow align="flex-start">
          <TextInput
            label="Invoice number"
            description="As printed on the paper they left you"
            value={invoiceNumber}
            onChange={(e) => setInvoiceNumber(e.currentTarget.value)}
            disabled={post.isPending}
          />
          <DateInput
            label="Dated"
            value={invoiceDate || null}
            onChange={(v) => {
              const next = v ?? '';
              setInvoiceDate(next);
              if (!dueTouched) setDueDate(dueFromTerms(next, supplier?.paymentTermsDays));
            }}
            valueFormat="D MMM YYYY"
            popoverProps={{ withinPortal: true }}
            disabled={post.isPending}
          />
          <DateInput
            label="To be paid by"
            description={
              supplier?.paymentTermsDays
                ? `Worked out from their ${supplier.paymentTermsDays} days`
                : 'Leave blank if you paid on delivery'
            }
            value={dueDate || null}
            onChange={(v) => {
              setDueTouched(true);
              setDueDate(v ?? '');
            }}
            valueFormat="D MMM YYYY"
            clearable
            popoverProps={{ withinPortal: true }}
            disabled={post.isPending}
          />
        </Group>

        <PaperPicker
          value={paper}
          onChange={setPaper}
          kind="photo"
          label="Photograph of the delivery note"
          description="Optional. The paper the driver leaves — what to look at when the count and the invoice do not agree."
          disabled={post.isPending}
        />

        <Group grow align="flex-start">
          <Select
            label="They billed you in"
            required
            data={enumOptions('currency')}
            value={currency}
            onChange={(v) => setCurrency(v ?? 'HNL')}
            allowDeselect={false}
            comboboxProps={{ withinPortal: true }}
            disabled={post.isPending}
          />
          <NumberInput
            label="Exchange rate used"
            description={
              latestFx && fxRate === ''
                ? 'The latest rate on file. Change it if the day they billed you was different.'
                : 'Lempiras per dollar on the day they billed you'
            }
            required
            value={fxRate === '' && latestFx ? latestFx / FX_SCALE : fxRate}
            onChange={setFxRate}
            decimalScale={6}
            disabled={post.isPending}
          />
        </Group>

        <div>
          <Text size="sm" fw={500}>
            What arrived
          </Text>
          <Text size="xs" c="dimmed" mb={6}>
            Each line is measured in the unit that item is already counted in. The price is what one of those
            costs on their invoice — it is what the recipes and the shelf value are worked out from.
          </Text>
          <Stack gap="xs">
            {lines.map((line, index) => {
              const unit = stock.unitOf(line.inventoryItemId);
              const lineTotal = Math.round(
                (toMinorUnits(line.unitPrice) ?? 0) * (typedNumber(line.qty) ?? 0),
              );
              return (
                <Paper key={line.key} withBorder p="xs" radius="sm">
                  <Group gap="xs" align="flex-end" wrap="wrap">
                    <div style={{ minWidth: 240, flex: 1 }}>
                      <EntitySelect
                        entity="inventoryItem"
                        label="Item"
                        value={line.inventoryItemId || null}
                        // Changing the item breaks the tie to the order line, because it is no
                        // longer a delivery of that line: what was ordered stays outstanding.
                        onChange={(v) =>
                          setLine(index, {
                            inventoryItemId: v ?? '',
                            purchaseOrderLineId: null,
                            orderedQty: undefined,
                            stillDueQty: undefined,
                          })
                        }
                        disabled={post.isPending}
                      />
                    </div>
                    <NumberInput
                      label="How many"
                      // What this delivery was expected to bring, against the box holding what
                      // turned up: a short delivery is then one number corrected, and the
                      // correction is visible. On a second delivery that is the remainder, not the
                      // whole order — the order line says both.
                      description={stillToCome(line, unit)?.expected}
                      min={0}
                      style={{ width: 150 }}
                      suffix={unit ? ` ${unit}` : undefined}
                      value={line.qty}
                      onChange={(v) => setLine(index, { qty: v })}
                      disabled={post.isPending}
                    />
                    <NumberInput
                      label={unit ? `Price per ${unit}` : 'Price each'}
                      min={0}
                      style={{ width: 160 }}
                      prefix={`${currencySymbol(currency)} `}
                      decimalScale={2}
                      thousandSeparator=","
                      hideControls
                      value={line.unitPrice}
                      onChange={(v) => setLine(index, { unitPrice: v })}
                      disabled={post.isPending}
                    />
                    <Text size="sm" fw={600} pb={6} style={{ width: 110, textAlign: 'right' }}>
                      {lineTotal > 0 ? formatMoney(lineTotal, currency) : ''}
                    </Text>
                    {lines.length > 1 && (
                      <Button
                        size="compact-sm"
                        variant="subtle"
                        color="red"
                        onClick={() => setLines(lines.filter((_, i) => i !== index))}
                        disabled={post.isPending}
                      >
                        Remove
                      </Button>
                    )}
                  </Group>
                </Paper>
              );
            })}
            <Group justify="space-between">
              <Button
                size="compact-sm"
                variant="light"
                onClick={() => setLines([...lines, blankLine()])}
                disabled={post.isPending}
              >
                Add another item
              </Button>
              <Text size="sm">
                What arrived comes to <strong>{formatMoney(goodsTotalMinor, currency)}</strong>
              </Text>
            </Group>
          </Stack>
        </div>

        {/*
          WHAT THE PAPER SAYS, beside what arrived.
          Recording seven cases when the invoice says ten used to open a bill for seven: a payable
          that matches no statement the vendor will ever send, and a credit next month with nothing
          to come off. Left blank this behaves exactly as it always did — the box is only for the
          morning the two disagree, and that morning it is the whole point of the screen.
        */}
        <Paper withBorder p="sm" radius="sm">
          {/*
            THE MISSING CASES TURNING UP, and only when there are some. A vendor with nothing
            outstanding against a delivery is never asked this — the answer would always be no, and
            a question whose answer is already known is a question that stops being read.
          */}
          {shortfallChoices.length > 0 && (
            <Select
              label="Were these already billed?"
              description="Choose the bill when this is the rest of a delivery they charged for and did not send"
              placeholder="No — this delivery has its own invoice"
              data={shortfallChoices}
              value={settlesBillId}
              onChange={setAlreadyBilledOn}
              clearable
              comboboxProps={{ withinPortal: true }}
              disabled={post.isPending}
              mb="sm"
            />
          )}

          {settlesBillId ? (
            <Alert color="blue" variant="light" title="Nothing new will be owed">
              <Stack gap={4}>
                <Text size="sm">
                  These goods come off what that bill was billed for and had not delivered, so no second bill
                  is opened for them. {formatMoney(shortfallMinor, currency)} of it is still outstanding and
                  this delivery is {formatMoney(goodsTotalMinor, currency)} of that.
                </Text>
                {goodsTotalMinor > shortfallMinor && (
                  <Text size="sm" fw={500}>
                    That is more than the bill was short. Record the rest as a delivery of its own — it is
                    goods they have not charged for yet.
                  </Text>
                )}
              </Stack>
            </Alert>
          ) : (
            <>
              <Group grow align="flex-start">
                <NumberInput
                  label="Total printed on their invoice"
                  description="Leave it blank if it agrees with what arrived. Copy it exactly as printed when it does not."
                  placeholder={formatMoney(goodsTotalMinor, currency)}
                  min={0}
                  prefix={`${currencySymbol(currency)} `}
                  decimalScale={2}
                  thousandSeparator=","
                  hideControls
                  value={invoiceTotal}
                  onChange={setInvoiceTotal}
                  disabled={post.isPending}
                />
                {disagrees && (
                  <Select
                    label="What the difference is"
                    description="Only you know this, and it decides what happens next"
                    required
                    placeholder="Say what it is"
                    data={reasonChoices}
                    value={reason}
                    onChange={setDiscrepancyReason}
                    comboboxProps={{ withinPortal: true }}
                    disabled={post.isPending}
                  />
                )}
              </Group>

              {/* Short, and the box above untouched. Not a refusal and not an error — a vendor who
                  invoices what he actually sent is ordinary, and blank is right then. It says
                  which of the two bills is about to open, while both are still one keystroke
                  apart. */}
              {shortAndUnstated.length > 0 && (
                <Alert
                  color="yellow"
                  variant="light"
                  mt="sm"
                  title="Less turned up than this delivery was due to bring"
                >
                  <Stack gap={4}>
                    {shortAndUnstated.map((g) => (
                      <Text key={g.said} size="sm">
                        {g.said}
                      </Text>
                    ))}
                    <Text size="sm">
                      Left as it is, their bill is recorded as{' '}
                      <strong>{formatMoney(goodsTotalMinor, currency)}</strong> — what arrived. If the invoice
                      in your hand still charges for all of it, copy its printed total into the box above
                      instead, and the difference is kept rather than lost.
                    </Text>
                  </Stack>
                </Alert>
              )}

              {disagrees && (
                <Stack gap="xs" mt="sm">
                  <Alert
                    color={differenceMinor > 0 ? 'orange' : 'blue'}
                    variant="light"
                    title={
                      differenceMinor > 0
                        ? `Their invoice is ${formatMoney(differenceMinor, currency)} more than what arrived`
                        : `Their invoice is ${formatMoney(-differenceMinor, currency)} less than what arrived`
                    }
                  >
                    <Stack gap={4}>
                      {/* Said in cases as well as in money, because the count is what can be checked
                      against the pallet while the driver is still standing there. */}
                      {inGoods.map((g) => (
                        <Text key={g.said} size="sm">
                          {g.said}
                        </Text>
                      ))}
                      <Text size="sm">
                        The bill will be recorded as <strong>{formatMoney(billedMinor, currency)}</strong>,
                        which is what they will chase you for, and the shelf will show what actually arrived.
                        Both are kept, and the difference stays on the list of bills that do not agree until
                        it is dealt with.
                      </Text>
                    </Stack>
                  </Alert>
                  <TextInput
                    label="Anything else worth noting"
                    description="For whoever reads this against their statement later"
                    placeholder="Driver said the rest is on Thursday’s boat"
                    value={discrepancyNote}
                    onChange={(e) => setDiscrepancyNote(e.currentTarget.value)}
                    disabled={post.isPending}
                  />
                </Stack>
              )}
            </>
          )}
        </Paper>

        {/* This read "a delivery cannot be edited afterwards", and told whoever was standing at the
            door that the only way to put a wrong one right was to count the area again. That stopped
            being true the day corrections shipped, and it is the more expensive of the two things it
            could have said wrong: recounting fixes the shelf and leaves the money, which is the dead
            end the correction exists to remove. The list this form opens from already says a
            delivery can be corrected — a screen contradicting itself is worse than either sentence
            on its own. What is still worth saying before the button is pressed is why care is
            cheaper than a correction: the cost typed here follows the goods into every recipe. */}
        <Alert color="yellow" variant="light" title="Check the amounts before you record it">
          The cost you enter here follows the goods into every recipe that uses them. If it does turn out to
          have been written down wrong, open the delivery afterwards and correct it — the shelf, the bill and
          the order all follow, and what was typed at the door is kept.
        </Alert>

        {error && (
          <Text size="sm" c="red">
            {error}
          </Text>
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={post.isPending}>
            Cancel
          </Button>
          <Button loading={post.isPending} onClick={submit}>
            Record the delivery
          </Button>
        </Group>
      </Stack>
      <WritingIntoAFiledMonth
        warning={filedMonthWarning}
        saving={post.isPending}
        onCancel={() => setFiledMonthWarning(null)}
        onConfirm={(filedMonthReason) => post.mutate({ filedMonthReason })}
      />
    </Modal>
  );
}
